import { inject, ChangeDetectionStrategy, computed, signal, Component, OnInit, OnChanges, SimpleChanges, input } from '@angular/core';
import { LoggerService } from '@/core/services/logger.service';

import { AbstractControl, FormBuilder, ReactiveFormsModule, FormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardContent } from '@/shared/components/card-content/card-content';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { InputMaskModule } from 'primeng/inputmask';
import { TableModule } from 'primeng/table';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TooltipModule } from 'primeng/tooltip';
import { BotaoAcoes } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { MenuItem } from 'primeng/api';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { ConflitoInteresseFeatureService } from './conflito-interesse.service';
import { MedicoConflitoInteresseResponse } from '@/core/api/sicom-api/registro/medico/conflito-interesse/conflito-interesse.model';
import { finalize } from 'rxjs/operators';
import { FormularioValidatorUtil } from '@/shared/utils/formulario-validator-util';
import { CustomTableComponent, TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';
import { CardContainer } from '@/shared/components/card-container/card-container';
import { TagModule } from 'primeng/tag';

interface EmpresaCadastrada {
    razaoSocial: string;
    cnpj: string;
    dataInicio: string; // dd/MM/yyyy
    dataFim?: string; // dd/MM/yyyy
    indiceRegistro?: number;
}

interface EmpresaCadastradaTabela extends EmpresaCadastrada {
    situacao: { value: string; color: string };
}

/** Abas do quadro de empresas. Conjunto fechado — evita a string solta espalhada pela tela. */
export enum EAbaConflito {
    ATIVAS = 'ATIVAS',
    INATIVAS = 'INATIVAS'
}

import { TabContainer, TabContent, TabContainerOptionType } from '@/shared/components/layout/tab-container/tab-container';

@Component({
    selector: 'app-conflito-interesse',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        FormsModule,
        TableModule,
        ButtonModule,
        CardContent,
        SelectModule,
        InputTextModule,
        InputMaskModule,
        SelectButtonModule,
        TooltipModule,
        BotaoAcoes,
        CustomTableComponent,
        CardContainer,
        TagModule,
        TabContainer,
        TabContent,
        CanDirective
    ],
    templateUrl: './conflito-interesse.html',
    styleUrl: './conflito-interesse.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
/** Aba de conflito de interesse do médico (empresas por vínculo, abas ativas/inativas). */
export class ConflitoInteresseComponent implements OnInit, OnChanges {
    private readonly logger = inject(LoggerService);
    pessoa = input<Pessoa>();
    protected readonly roleConflitoEditar = PERMISSOES.medicos.conflitoInteresse.editar;
    private readonly fb = inject(FormBuilder);
    readonly conflitoInteresseForm = this.fb.group(
        {
            cnpj: [''],
            razaoSocial: [''],
            emailEmpresa: [''],
            naturezaVinculo: this.fb.control<number | null>(null),
            dataInicio: ['', [this.dataValidaValidator()]],
            dataFim: ['', [this.dataValidaValidator()]]
        },
        { validators: [this.periodoValidoValidator()] }
    );
    /**
     * Estado que a tela lê fica em signal — o componente é OnPush e boa parte destas alterações
     * nasce em callback assíncrono (resposta de HTTP) ou em `command` de menu, que não marcam a
     * view como suja. Com campo simples a tela continuava mostrando o estado anterior.
     */
    readonly modoEdicao = signal(false);
    /** `string`, e não a união, porque faz two-way com o `selected` do tab-container (`model<string>`). */
    readonly abaSelecionada = signal<string>(EAbaConflito.ATIVAS);
    /** Controla se estamos criando um novo registro (true) ou editando o atual (false) */
    private criandoNovo = false;

    // Opções para seleção de aba
    optionsTab: TabContainerOptionType[] = [
        { label: 'Ativas', value: 'ATIVAS' },
        { label: 'Inativas', value: 'INATIVAS' }
    ];

    // Opções de Natureza do Vínculo carregadas via API
    readonly opcoesNaturezaVinculo = signal<{ label: string; value: number }[]>([]);
    loadingNaturezas = signal(false);
    readonly erroNaturezas = signal<string | undefined>(undefined);

    readonly empresasAtivas = signal<EmpresaCadastrada[]>([]);
    readonly empresasInativas = signal<EmpresaCadastrada[]>([]);

    // Estados
    loading = signal(false);
    erro?: string;
    saving = signal(false);

    readonly mostrandoFormulario = signal(false);

    // Navegação entre registros de Conflito de Interesse
    readonly registrosConflito = signal<MedicoConflitoInteresseResponse[]>([]);
    private indiceAtual: number = 0;

    // Itens do menu de ações no cabeçalho (Novo/Deletar)
    acoesNovo: MenuItem[] = [
        {
            label: 'Novo',
            icon: 'pi pi-plus',
            styleClass: 'bg-green-500',
            command: () => this.abrirDialogNovo()
        }
    ];
    acoesFormulario: MenuItem[] = [
        {
            label: 'Editar',
            icon: 'pi pi-pencil',
            styleClass: 'bg-blue-500',
            command: () => this.habilitarEdicao()
        },
        {
            label: 'Remover',
            icon: 'pi pi-trash',
            styleClass: 'bg-red-500',
            command: () => this.deletar()
        }
    ];

    private readonly feature = inject(ConflitoInteresseFeatureService);

    ngOnInit(): void {
        const p = this.pessoa();
        // Carrega naturezas de vínculo (não depende da pessoa)
        this.carregarNaturezasVinculo();
        if (p && p.id != null && !!p.uf) this.carregar(p);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['pessoa']) {
            const p = this.pessoa();
            if (p && p.id != null && !!p.uf) this.carregar(p);
            else {
                this.empresasAtivas.set([]);
                this.empresasInativas.set([]);
            }
        }
    }

    abrirDialogNovo(): void {
        if (this.abaSelecionada() !== EAbaConflito.ATIVAS) return;
        this.novo();
        this.mostrandoFormulario.set(true);
    }

    abrirDialogRegistro(item: EmpresaCadastrada): void {
        if (this.modoEdicao() || item.indiceRegistro == null) return;
        this.selecionarRegistro(item);
        this.mostrandoFormulario.set(true);
    }

    fecharDialogConflito(): void {
        this.mostrandoFormulario.set(false);
        if (this.modoEdicao()) this.cancelarEdicao();
    }

    habilitarEdicao() {
        // Não permitir edição quando visualizando registros inativos
        if (this.abaSelecionada() !== EAbaConflito.ATIVAS) {
            return;
        }
        // Em edição de registro existente não aplicamos validações de inclusão
        this.limparValidadoresInclusao();
        this.criandoNovo = false;
        this.modoEdicao.set(true);
    }

    salvarDados() {
        if (this.conflitoInteresseForm.invalid) {
            this.conflitoInteresseForm.markAllAsTouched();
            return;
        }
        const p = this.pessoa();
        if (!p || p.id == null || !p.uf) {
            // Sem pessoa válida, não é possível prosseguir
            this.logger.warn('Pessoa não informada para upsert de Conflito de Interesse');
            return;
        }

        const atual = this.criandoNovo ? undefined : this.registrosConflito()[this.indiceAtual];
        const form = this.conflitoInteresseForm.value as {
            cnpj?: string;
            razaoSocial?: string;
            emailEmpresa?: string;
            naturezaVinculo?: number | null;
            dataInicio?: string;
            dataFim?: string;
        };

        const toIso = (v?: string | null): string | null => {
            const s = typeof v === 'string' ? v.trim() : '';
            if (!s) return null;
            const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
            if (!m) return null;
            const [_, dd, mm, yyyy] = m;
            return `${yyyy}-${mm}-${dd}`;
        };
        const onlyDigits = (s?: string | null): string | undefined => {
            if (!s) return undefined;
            return String(s).replace(/\D+/g, '') || undefined;
        };

        const payload: MedicoConflitoInteresseResponse = {
            idConflitoInteresse: atual?.idConflitoInteresse ?? undefined,
            idMedico: p.id as number,
            sgUf: p.uf,
            nuCnpj: onlyDigits(form.cnpj),
            nmEmpresa: form.razaoSocial ?? undefined,
            nmEmail: form.emailEmpresa ?? undefined,
            idNaturezaVinculo: form.naturezaVinculo ?? undefined,
            dtInicio: toIso(form.dataInicio),
            dtFim: toIso(form.dataFim)
        };

        this.saving.set(true);
        this.feature
            .upsertConflitoInteresse(payload)
            .pipe(
                finalize(() => {
                    this.saving.set(false);
                })
            )
            .subscribe({
                next: (ok) => {
                    if (ok) {
                        this.modoEdicao.set(false);
                        this.criandoNovo = false;
                        this.limparValidadoresInclusao();
                        this.mostrandoFormulario.set(false);
                        const pessoa = this.pessoa();
                        if (pessoa && pessoa.id != null && pessoa.uf) {
                            this.carregar({ id: pessoa.id, uf: pessoa.uf });
                        }
                    } else {
                        // 409 tratado como false
                        this.logger.warn('Não foi possível persistir o registro de Conflito de Interesse (409).');
                    }
                },
                error: (err) => {
                    this.logger.error('Falha ao persistir Conflito de Interesse', err);
                }
            });
    }

    cancelarEdicao() {
        this.conflitoInteresseForm.reset();
        this.modoEdicao.set(false);
        this.criandoNovo = false;
        this.limparValidadoresInclusao();
        // Recarrega o registro atual no formulário
        this.renderizarRegistroAtual();
        this.fecharDialogConflito();
    }

    novo() {
        // Não permitir criação quando visualizando registros inativos
        if (this.abaSelecionada() !== EAbaConflito.ATIVAS) {
            return;
        }
        this.criandoNovo = true;
        this.modoEdicao.set(true);
        this.conflitoInteresseForm.reset();
        // Em inclusão, os campos exigidos passam a ser obrigatórios
        this.aplicarValidadoresInclusao();
    }

    deletar() {
        this.logger.info('Deletar registro');
    }

    readonly empresasExibidas = computed<EmpresaCadastrada[]>(() => (this.abaSelecionada() === EAbaConflito.ATIVAS ? this.empresasAtivas() : this.empresasInativas()));

    readonly empresasExibidasTabela = computed<EmpresaCadastradaTabela[]>(() => {
        const situacao = this.abaSelecionada() === EAbaConflito.ATIVAS ? { value: 'Ativa', color: 'var(--p-green-500)' } : { value: 'Inativa', color: 'var(--p-red-500)' };
        return this.empresasExibidas().map((item) => ({ ...item, situacao }));
    });

    /** Campo, e não getter: o getter refazia o objeto a cada detecção de mudanças. */
    readonly metadataTabela: TableMetadata = {
        headers: {
            razaoSocial: { type: 'text', default: '-', hidden: false, value: 'Razão Social' },
            cnpj: { type: 'text', default: '-', hidden: false, value: 'CNPJ' },
            dataInicio: { type: 'text', default: '-', hidden: false, value: 'Data Início' },
            dataFim: { type: 'text', default: '-', hidden: false, value: 'Data Fim' },
            situacao: { type: 'tag', default: '-', hidden: false, value: 'Situação' }
        },
        title: 'razaoSocial',
        tag: 'situacao',
        actions: {
            visualizar: {
                label: 'Visualizar',
                icon: 'pi pi-eye',
                color: 'success',
                command: (item: EmpresaCadastradaTabela) => this.abrirDialogRegistro(item)
            }
        }
    };

    obterRazaoSocialExibicao(item: { razaoSocial?: string }): string {
        const r = item.razaoSocial || '';
        const limit = r.length > 70 ? r.substring(0, 70) + '...' : r;
        return limit.toUpperCase();
    }

    onToggleAba(novaAba?: string | { label: string; value: string }): void {
        const valorAba = typeof novaAba === 'object' ? novaAba.value : novaAba;

        // Força a seleção local da aba ao receber o evento para evitar "reversão" visual
        if (valorAba === EAbaConflito.ATIVAS || valorAba === EAbaConflito.INATIVAS) {
            this.abaSelecionada.set(valorAba);
        }
        const p = this.pessoa();
        if (p && p.id != null && !!p.uf) this.carregar(p);
    }

    selecionarRegistro(item: EmpresaCadastrada): void {
        if (item.indiceRegistro == null) return;
        this.indiceAtual = item.indiceRegistro;
        this.renderizarRegistroAtual();
    }

    /**
     * Em inclusão (novo cadastro), os campos CNPJ, Razão Social, E-mail, Natureza do Vínculo e Data Início são obrigatórios.
     */
    private aplicarValidadoresInclusao(): void {
        const campos = ['cnpj', 'razaoSocial', 'emailEmpresa', 'naturezaVinculo', 'dataInicio'] as const;
        campos.forEach((nome) => {
            const ctrl = this.conflitoInteresseForm.get(nome as string);
            if (ctrl) {
                if (nome === 'cnpj') {
                    ctrl.setValidators([Validators.required, this.cnpjValidoValidator()]);
                } else if (nome === 'emailEmpresa') {
                    ctrl.setValidators([Validators.required, this.emailValidoValidator()]);
                } else if (nome === 'dataInicio') {
                    ctrl.setValidators([Validators.required, this.dataValidaValidator()]);
                } else {
                    ctrl.setValidators([Validators.required]);
                }
                ctrl.updateValueAndValidity({ emitEvent: false });
            }
        });
    }

    /**
     * Remove os validadores de inclusão (obrigatoriedade) para não impactar a edição de registros existentes.
     */
    private limparValidadoresInclusao(): void {
        const campos = ['cnpj', 'razaoSocial', 'emailEmpresa', 'naturezaVinculo', 'dataInicio'] as const;
        campos.forEach((nome) => {
            const ctrl = this.conflitoInteresseForm.get(nome as string);
            if (ctrl) {
                ctrl.setValidators(nome === 'dataInicio' ? [this.dataValidaValidator()] : []);
                ctrl.updateValueAndValidity({ emitEvent: false });
            }
        });
    }

    private carregar(pessoa: Pessoa): void {
        this.loading.set(true);
        this.erro = undefined;
        // Guardado aqui, e não lido de novo na resposta: se a aba mudar durante a requisição, o
        // resultado ainda pertence à aba que o pediu e não pode cair na lista da outra.
        const abaDaBusca = this.abaSelecionada();
        const obs = abaDaBusca === EAbaConflito.ATIVAS ? this.feature.buscar({ id: pessoa.id, uf: pessoa.uf }) : this.feature.buscarInativo({ id: pessoa.id, uf: pessoa.uf });

        obs.subscribe({
            next: (resp: MedicoConflitoInteresseResponse[]) => {
                // Prepara lista para navegação, ordenada por dtInicio desc
                const lista = (resp || []).slice();
                const parseDt = (v?: string | null) => (v ? new Date(v).getTime() : 0);
                lista.sort((a, b) => parseDt(b.dtInicio) - parseDt(a.dtInicio));
                this.registrosConflito.set(lista);
                this.indiceAtual = 0;
                this.renderizarRegistroAtual();

                const linhas: EmpresaCadastrada[] = lista.map((r, idx) => ({
                    razaoSocial: r.nmEmpresa ?? '',
                    cnpj: r.nuCnpj ?? '',
                    dataInicio: this.formatarData(r.dtInicio),
                    dataFim: this.formatarData(r.dtFim),
                    indiceRegistro: idx
                }));

                // Quem separa ativo de inativo é o endpoint. Reclassificar por `dtFim` aqui fazia o
                // registro que não batia com a regra local cair na lista da OUTRA aba — e como só a
                // lista da aba pedida está em tela, ele simplesmente sumia. A resposta alimenta a
                // lista da aba que a pediu, e só ela.
                if (abaDaBusca === EAbaConflito.ATIVAS) this.empresasAtivas.set(linhas);
                else this.empresasInativas.set(linhas);
            },
            error: (err: Error) => {
                this.logger.error('Erro ao buscar conflito de interesse', err);
                this.erro = err.message || 'Falha ao carregar informações de conflito de interesse.';
                this.empresasAtivas.set([]);
                this.empresasInativas.set([]);
                this.registrosConflito.set([]);
                this.indiceAtual = 0;
            }
        }).add(() => {
            this.loading.set(false);
        });
    }

    private formatarData(valor?: string | null): string {
        if (!valor) return '';
        const m = String(valor).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) {
            const [, yyyy, mm, dd] = m;
            return `${dd}/${mm}/${yyyy}`;
        }
        try {
            const d = new Date(valor);
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            return `${dd}/${mm}/${yyyy}`;
        } catch {
            return String(valor ?? '');
        }
    }

    // Carrega lista de Naturezas de Vínculo a partir da API
    private carregarNaturezasVinculo(): void {
        this.loadingNaturezas.set(true);
        this.erroNaturezas.set(undefined);
        this.feature
            .listarNaturezasVinculo()
            .subscribe({
                next: (lista) => {
                    this.opcoesNaturezaVinculo.set(
                        (lista || []).map((n) => ({
                            label: n.nmNaturezaVinculo,
                            value: n.idNaturezaVinculo
                        }))
                    );
                },
                error: (err: Error) => {
                    this.logger.error('Erro ao carregar naturezas de vínculo', err);
                    this.opcoesNaturezaVinculo.set([]);
                    this.erroNaturezas.set('Falha ao carregar naturezas de vínculo.');
                }
            })
            .add(() => {
                this.loadingNaturezas.set(false);
            });
    }

    // Navegação e preenchimento do formulário com o registro atual
    private renderizarRegistroAtual(): void {
        const registro = this.registrosConflito()[this.indiceAtual];
        if (registro) this.preencherFormularioConflito(registro);
    }

    private preencherFormularioConflito(r: MedicoConflitoInteresseResponse): void {
        if (!this.conflitoInteresseForm) return;
        this.conflitoInteresseForm.patchValue(
            {
                cnpj: r.nuCnpj ?? '',
                razaoSocial: r.nmEmpresa ?? '',
                emailEmpresa: r.nmEmail ?? '',
                naturezaVinculo: r.idNaturezaVinculo ?? null,
                dataInicio: this.formatarData(r.dtInicio),
                dataFim: this.formatarData(r.dtFim)
            },
            { emitEvent: false }
        );
    }

    private cnpjValidoValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const valor = String(control.value ?? '').trim();
            if (!valor) {
                return null;
            }

            return FormularioValidatorUtil.cnpjValido(valor) ? null : { cnpjInvalido: true };
        };
    }

    private emailValidoValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const valor = String(control.value ?? '').trim();
            if (!valor) {
                return null;
            }

            return FormularioValidatorUtil.emailValido(valor) ? null : { emailInvalido: true };
        };
    }

    private dataValidaValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const valor = String(control.value ?? '').trim();
            if (!valor) {
                return null;
            }

            return this.parseDataBr(valor) ? null : { dataInvalida: true };
        };
    }

    /**
     * Validador de período do FormGroup: garante que a Data Início não seja superior à Data Fim.
     * Ignora a checagem quando qualquer uma das datas estiver vazia ou inválida.
     */
    private periodoValidoValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const inicio = this.parseDataBr(control.get('dataInicio')?.value);
            const fim = this.parseDataBr(control.get('dataFim')?.value);

            if (!inicio || !fim) {
                return null;
            }

            return inicio.getTime() > fim.getTime() ? { periodoInvalido: true } : null;
        };
    }

    private parseDataBr(valor: unknown): Date | null {
        const texto = String(valor ?? '').trim();
        const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(texto);
        if (!match) {
            return null;
        }

        const [, dd, mm, yyyy] = match;
        const data = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
        data.setHours(0, 0, 0, 0);

        if (data.getFullYear() !== Number(yyyy) || data.getMonth() !== Number(mm) - 1 || data.getDate() !== Number(dd)) {
            return null;
        }

        return data;
    }

    campoInvalido(nomeCampo: string): boolean {
        const campo = this.conflitoInteresseForm?.get(nomeCampo);
        if (!campo) return false;

        if (nomeCampo === 'dataFim' && this.conflitoInteresseForm?.hasError('periodoInvalido')) {
            const inicio = this.conflitoInteresseForm.get('dataInicio');
            return !!(campo.touched || campo.dirty || inicio?.touched || inicio?.dirty);
        }

        return campo.invalid && (campo.touched || campo.dirty);
    }

    mensagemErroCampo(nomeCampo: string): string | null {
        const campo = this.conflitoInteresseForm?.get(nomeCampo);
        if (nomeCampo === 'dataFim' && this.conflitoInteresseForm?.hasError('periodoInvalido')) {
            return 'A data de início não pode ser superior à data fim.';
        }

        if (!campo?.errors) return null;

        if (campo.errors['required']) return 'Campo obrigatório.';
        if (campo.errors['cnpjInvalido']) return 'CNPJ inválido.';
        if (campo.errors['emailInvalido']) return 'E-mail inválido.';
        if (campo.errors['dataInvalida']) return 'Data inválida.';
        return 'Valor inválido.';
    }
}
